/**
 * Cache key generation utilities for multi-tenant restaurant system
 * Ensures proper isolation between users, restaurants, and branches
 */

export interface UserContext {
  userId: string;
  restaurantId: string;
  isStaff: boolean;
  allowedBranches: string[];
}

export type CacheDataType = 
  | 'branches' 
  | 'categories' 
  | 'menuItems' 
  | 'deliveryZones' 
  | 'orders'
  | 'tableSessionSummary';

/**
 * Generate a secure cache key that includes user context for isolation
 */
export const generateCacheKey = (
  userContext: UserContext,
  dataType: CacheDataType,
  additionalParams?: Record<string, string>
): string => {
  const userType = userContext.isStaff ? 'staff' : 'owner';
  const baseKey = `cache:${userType}:${userContext.userId}:${userContext.restaurantId}`;
  
  let key = `${baseKey}:${dataType}`;
  
  // Add additional parameters
  if (additionalParams) {
    const sortedParams = Object.entries(additionalParams)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${v}`)
      .join(':');
    
    if (sortedParams) {
      key += `:${sortedParams}`;
    }
  }
  
  return key;
};

/**
 * Generate cache key for branch-specific data
 */
export const generateBranchCacheKey = (
  userContext: UserContext,
  dataType: CacheDataType,
  branchId: string,
  additionalParams?: Record<string, string>
): string => {
  return generateCacheKey(userContext, dataType, {
    branchId,
    ...additionalParams
  });
};

/**
 * Generate cache key for category-specific menu items
 */
export const generateCategoryCacheKey = (
  userContext: UserContext,
  categoryId: string
): string => {
  return generateCacheKey(userContext, 'menuItems', { categoryId });
};

/**
 * Parse cache key to extract components
 */
export const parseCacheKey = (key: string) => {
  const parts = key.split(':');
  
  if (parts.length < 5 || parts[0] !== 'cache') {
    throw new Error('Invalid cache key format');
  }
  
  return {
    userType: parts[1] as 'owner' | 'staff',
    userId: parts[2],
    restaurantId: parts[3],
    dataType: parts[4] as CacheDataType,
    additionalParams: parts.slice(5)
  };
};

/**
 * Check if cache key belongs to user context
 */
export const validateCacheKeyAccess = (
  key: string,
  userContext: UserContext
): boolean => {
  try {
    const parsed = parseCacheKey(key);
    
    // Check user ID and restaurant ID match
    if (parsed.userId !== userContext.userId || 
        parsed.restaurantId !== userContext.restaurantId) {
      return false;
    }
    
    // Check user type consistency
    const expectedUserType = userContext.isStaff ? 'staff' : 'owner';
    if (parsed.userType !== expectedUserType) {
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
};

/**
 * Generate pattern for invalidating multiple cache keys
 */
export const generateCachePattern = (
  userContext: UserContext,
  dataType?: CacheDataType,
  branchId?: string
): string => {
  const userType = userContext.isStaff ? 'staff' : 'owner';
  let pattern = `cache:${userType}:${userContext.userId}:${userContext.restaurantId}`;
  
  if (dataType) {
    pattern += `:${dataType}`;
    
    if (branchId) {
      pattern += `:branchId:${branchId}`;
    }
  }
  
  return pattern;
};

/**
 * Cache key utilities for common data types
 */
export const CacheKeys = {
  branches: (userContext: UserContext) => 
    generateCacheKey(userContext, 'branches'),
    
  categories: (userContext: UserContext, branchId: string) => 
    generateBranchCacheKey(userContext, 'categories', branchId),
    
  menuItems: (userContext: UserContext, branchId: string, categoryId: string) => 
    generateCacheKey(userContext, 'menuItems', { branchId, categoryId }),
    
  deliveryZones: (userContext: UserContext, branchId: string) => 
    generateBranchCacheKey(userContext, 'deliveryZones', branchId),
    
  orders: (userContext: UserContext, branchId: string) => 
    generateBranchCacheKey(userContext, 'orders', branchId),
    
  tableSessionSummary: (userContext: UserContext, branchId: string) => 
    generateBranchCacheKey(userContext, 'tableSessionSummary', branchId)
};